/**
 * Bancada Previdenciária — estrutura das 5 etapas e 12 GenIAs
 *
 * IDs devem corresponder exatamente aos IDs do backend (api/agents/*.js).
 * O click no card emite chat-open-agent no chatBus com o id abaixo,
 * e o ChatWidget abre com aquela GenIA pré-selecionada.
 *
 * Categorias (cores aplicadas no BancadaGenIA.vue):
 *  - diagnostico     azul claro
 *  - nucleo          azul navy
 *  - aprofundamento  laranja
 *  - ponte           teal
 *  - recuperacao     roxo
 *  - saida           verde
 */

import imgCnis from '@/assets/genias/genia-cnis.jpg';
import imgContracheque from '@/assets/genias/genia-contracheque.jpg';
import imgRgps from '@/assets/genias/genia-rgps.jpg';
import imgRpps from '@/assets/genias/genia-rpps.jpg';
import imgMilitar from '@/assets/genias/genia-militar.jpg';
import imgEspecial from '@/assets/genias/genia-especial.jpg';
import imgCtc from '@/assets/genias/genia-ctc.jpg';
import imgAcordos from '@/assets/genias/genia-acordos.jpg';
import imgComplementar from '@/assets/genias/genia-complementar.jpg';
import imgRevisoes from '@/assets/genias/genia-revisoes.jpg';
import imgIsencaoIr from '@/assets/genias/genia-isencao-ir.jpg';
import imgProcessual from '@/assets/genias/genia-processual.jpg';

export const CATEGORY_LABELS = {
  diagnostico: 'Diagnóstico',
  nucleo: 'Núcleo',
  aprofundamento: 'Aprofundamento',
  ponte: 'Ponte',
  recuperacao: 'Recuperação',
  saida: 'Saída',
};

export const ETAPAS = [
  {
    numero: 1,
    titulo: 'Organizar documentos e prova',
    descricao: 'Comece pela leitura dos documentos para entender a situação contributiva.',
    genias: [
      {
        id: 'genia-cnis',
        nome: 'CNIS Milionário',
        descricao: 'Diagnóstico de extratos do CNIS e identificação de erros e omissões.',
        categoria: 'diagnostico',
        image: imgCnis,
      },
      {
        id: 'genia-contracheque',
        nome: 'Leitor de Contracheque',
        descricao: 'Leitura de holerites para cálculo de base de contribuição.',
        categoria: 'diagnostico',
        image: imgContracheque,
      },
    ],
  },
  {
    numero: 2,
    titulo: 'Definir o regime',
    descricao: 'Identifique se o caso é RGPS, RPPS ou militar.',
    genias: [
      {
        id: 'genia-rgps',
        nome: 'GenIA - RGPS',
        descricao: 'Trabalhador CLT, autônomo e contribuinte individual.',
        categoria: 'nucleo',
        image: imgRgps,
      },
      {
        id: 'genia-rpps',
        nome: 'GenIA - RPPS',
        descricao: 'Servidor público da União, Estados, DF e Municípios.',
        categoria: 'nucleo',
        image: imgRpps,
      },
      {
        id: 'genia-militar',
        nome: 'GenIA - Militar Invictus',
        descricao: 'Reforma e pensão militar das Forças Armadas e auxiliares.',
        categoria: 'nucleo',
        image: imgMilitar,
      },
    ],
  },
  {
    numero: 3,
    titulo: 'Situações especiais e pontes',
    descricao: 'Casos que exigem aprofundamento ou conexão entre regimes.',
    genias: [
      {
        id: 'genia-especial',
        nome: 'GenIA - Atividade Especial',
        descricao: 'Insalubridade, periculosidade e conversão de tempo especial.',
        categoria: 'aprofundamento',
        image: imgEspecial,
      },
      {
        id: 'genia-ctc',
        nome: 'GenIA - CTC v2',
        descricao: 'Auditoria e emissão de Certidão de Tempo de Contribuição.',
        categoria: 'ponte',
        image: imgCtc,
      },
      {
        id: 'genia-acordos',
        nome: 'GenIA - Acordos Internacionais',
        descricao: 'Tempo de contribuição entre países com acordo previdenciário.',
        categoria: 'ponte',
        image: imgAcordos,
      },
      {
        id: 'genia-complementar',
        nome: 'GenIA - Previdência Complementar',
        descricao: 'Fundos de pensão e regimes de previdência privada.',
        categoria: 'aprofundamento',
        image: imgComplementar,
      },
    ],
  },
  {
    numero: 4,
    titulo: 'Revisão e recuperação',
    descricao: 'Teses revisionais e recuperação de valores retidos.',
    genias: [
      {
        id: 'genia-revisoes',
        nome: 'GenIA - Revisões de Benefícios',
        descricao: 'Teses revisionais de benefícios já concedidos.',
        categoria: 'recuperacao',
        image: imgRevisoes,
      },
      {
        id: 'genia-isencao-ir',
        nome: 'GenIA - Isenção e Restituição de IR',
        descricao: 'Cálculo de restituição de IR retido indevidamente até 5 anos.',
        categoria: 'recuperacao',
        image: imgIsencaoIr,
      },
    ],
  },
  {
    numero: 5,
    titulo: 'Geração de peças',
    descricao: 'Montagem da peça jurídica para o caso.',
    genias: [
      {
        id: 'genia-processual',
        nome: 'GenIA - Processual Previdenciário',
        descricao: 'Montagem de peça jurídica previdenciária.',
        categoria: 'saida',
        image: imgProcessual,
      },
    ],
  },
];

/**
 * Lista plana de todas as GenIAs do mapa, útil para validações ou
 * para busca rápida por id (ex.: card de trilha apontando para um id).
 */
export const ALL_GENIAS = ETAPAS.flatMap((etapa) =>
  etapa.genias.map((g) => ({ ...g, etapa: etapa.numero })),
);

/**
 * Trilhas temáticas: fluxos pré-configurados para casos comuns.
 *
 * Cada passo aponta para um agentId existente em api/agents/*.js.
 * O click numa pill abre o chat com aquela GenIA.
 * O botão "Ver trilha →" abre a primeira GenIA do fluxo (passos[0]).
 *
 * NOTA: a validação contra agentes desativados acontece no
 * ChatWidget.openWithAgent (sempre confere this.agents).
 */
export const TRILHAS = [
  {
    id: 'apenas-cnis',
    titulo: 'Tenho só um CNIS',
    passos: [
      { agentId: 'genia-cnis', label: 'CNIS' },
      { agentId: 'genia-rgps', label: 'RGPS' },
      { agentId: 'genia-revisoes', label: 'Revisões' },
      { agentId: 'genia-processual', label: 'Processual' },
    ],
  },
  {
    id: 'servidor-contracheques',
    titulo: 'É servidor com contracheques',
    passos: [
      { agentId: 'genia-contracheque', label: 'Contracheque' },
      { agentId: 'genia-rpps', label: 'RPPS' },
      { agentId: 'genia-ctc', label: 'CTC' },
      { agentId: 'genia-processual', label: 'Processual' },
    ],
  },
  {
    id: 'ppp-tempo-especial',
    titulo: 'Há PPP e suspeita de tempo especial',
    passos: [
      { agentId: 'genia-cnis', label: 'CNIS' },
      { agentId: 'genia-rgps', label: 'RGPS' },
      { agentId: 'genia-especial', label: 'Ativ. Especial' },
      { agentId: 'genia-processual', label: 'Processual' },
    ],
  },
  {
    id: 'tempo-publico-privado',
    titulo: 'Tempo público e privado misturado',
    passos: [
      { agentId: 'genia-cnis', label: 'CNIS' },
      { agentId: 'genia-ctc', label: 'CTC' },
      { agentId: 'genia-rgps', label: 'RGPS' },
      { agentId: 'genia-rpps', label: 'RPPS' },
    ],
  },
  {
    id: 'beneficio-concedido',
    titulo: 'Benefício já concedido',
    passos: [
      { agentId: 'genia-revisoes', label: 'Revisões' },
      { agentId: 'genia-cnis', label: 'CNIS' },
      { agentId: 'genia-processual', label: 'Processual' },
    ],
  },
  {
    id: 'retencao-ir',
    titulo: 'Retenção indevida de IR',
    passos: [
      { agentId: 'genia-isencao-ir', label: 'IR' },
      { agentId: 'genia-processual', label: 'Processual' },
    ],
  },
  {
    id: 'caso-militar',
    titulo: 'É caso militar',
    passos: [
      { agentId: 'genia-militar', label: 'Militar' },
      { agentId: 'genia-ctc', label: 'CTC' },
      { agentId: 'genia-processual', label: 'Processual' },
    ],
  },
  {
    id: 'tempo-exterior',
    titulo: 'Há tempo no exterior',
    passos: [
      { agentId: 'genia-acordos', label: 'Acordos' },
      { agentId: 'genia-rgps', label: 'RGPS' },
      { agentId: 'genia-processual', label: 'Processual' },
    ],
  },
];
